/**
 * Coincidencia de rutas sin react-router.
 *
 * Hace falta porque las tres aplicaciones montan `SessionGuard` **envolviendo el router entero**, asi
 * que ahi dentro no hay `useLocation` que valga: lo unico disponible es `window.location`. Y lo que se
 * lee de ahi lleva el prefijo con el que la aplicacion cuelga del origen (`/identity`, `/website`,
 * `/backoffice`), mientras que las rutas que declara cada app -- y las que se escriben en una lista a
 * mano -- van sin el.
 *
 * La semantica es la que `j2l-website` ya tenia probada en su `shouldSkipDev401Redirect`, generalizada
 * sobre el basename en vez de llevar `/website` incrustado.
 */

/**
 * Quita el basename de un `pathname` y normaliza el resultado a algo comparable.
 *
 * El corte va con un lookahead (`(?=\/|$)`) a proposito: sin el, un basename `/website` se comeria
 * tambien el principio de `/websitefoo`, que es otra ruta distinta. La barra final se descarta para que
 * `/services/dashboard` y `/services/dashboard/` no sean dos cosas, y la raiz siempre sale como `'/'`.
 */
export const normalizePathname = (pathname: string, basename?: string): string => {
  let value = pathname || '/';

  const prefix = (basename || '').replace(/\/+$/, '');
  if (prefix.length > 0 && prefix !== '/') {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    value = value.replace(new RegExp(`^${escaped}(?=/|$)`), '');
  }

  value = value.replace(/\/+$/, '');

  return value.length > 0 ? value : '/';
};

/**
 * Si `pathname` es una de `paths`, o cuelga de una de ellas.
 *
 * «Cuelga de» es prefijo **seguido de barra**, no `startsWith` a secas: `/shortlists/demo` tiene que
 * cubrir `/shortlists/demo/contact` sin cubrir `/shortlists/demos`. Y por eso la comparacion es esta y
 * no una de sufijo, que seria lo comodo teniendo el basename delante: con sufijo, `'/'` casaria con
 * todo, y la home publica del website es justamente una de las rutas que hay que poder nombrar.
 */
export const matchesAnyPath = (pathname: string, paths: string[] | undefined, basename?: string): boolean => {
  if (paths == null || paths.length === 0) return false;

  const value = normalizePathname(pathname, basename);

  return paths.some((path) => {
    const candidate = normalizePathname(path);
    return value === candidate || (candidate !== '/' && value.startsWith(`${candidate}/`));
  });
};
